import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, TrashIcon, CheckCircleIcon, ShieldCheckIcon, PencilIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import ConfirmDialog from '../components/ConfirmDialog';

const GrievanceCommitmentPage = () => {
    const [rights, setRights] = useState([]);
    const [settings, setSettings] = useState({
        title: '',
        description: '',
        promise_title: '',
        promise_text: ''
    });
    const [loading, setLoading] = useState(true);
    const [savingSettings, setSavingSettings] = useState(false);
    const [newRight, setNewRight] = useState('');
    const [addingRight, setAddingRight] = useState(false);
    const [editingRight, setEditingRight] = useState(null); // { id: 1, text: '...' }
    const [updatingRightId, setUpdatingRightId] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, rightId: null });

    const fetchData = async () => {
        try {
            setLoading(true);
            const [rightsRes, settingsRes] = await Promise.all([
                axios.get('/api/admin/grievance-customer-rights'),
                axios.get('/api/admin/grievance-commitment/settings')
            ]);
            setRights(rightsRes.data.data || []);
            if (settingsRes.data.data) {
                setSettings(settingsRes.data.data);
            }
        } catch (error) {
            console.error('Error fetching commitment data:', error);
            toast.error('Failed to load commitment data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSettingsSave = async () => {
        try {
            setSavingSettings(true);
            await axios.put('/api/admin/grievance-commitment/settings', settings);
            toast.success('Settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSavingSettings(false);
        }
    };

    const handleAddRight = async (e) => {
        e.preventDefault();
        if (!newRight.trim()) return;
        try {
            setAddingRight(true);
            await axios.post('/api/admin/grievance-customer-rights', {
                right_text: newRight,
                display_order: rights.length + 1,
                is_active: true
            });
            toast.success('Right added successfully');
            setNewRight('');
            fetchData();
        } catch (error) {
            console.error('Error adding right:', error);
            toast.error('Failed to add right');
        } finally {
            setAddingRight(false);
        }
    };

    const handleUpdateRight = async (right) => {
        if (!editingRight?.text?.trim()) return;
        try {
            setUpdatingRightId(right.id);
            await axios.put(`/api/admin/grievance-customer-rights/${right.id}`, {
                ...right,
                right_text: editingRight.text
            });
            toast.success('Right updated successfully');
            setEditingRight(null);
            fetchData();
        } catch (error) {
            console.error('Error updating right:', error);
            toast.error('Failed to update right');
        } finally {
            setUpdatingRightId(null);
        }
    };

    const handleDeleteRight = (id) => {
        setDeleteConfirm({ isOpen: true, rightId: id });
    };

    const confirmDeleteRight = async () => {
        try {
            await axios.delete(`/api/admin/grievance-customer-rights/${deleteConfirm.rightId}`);
            toast.success('Right removed successfully');
            setDeleteConfirm({ isOpen: false, rightId: null });
            fetchData();
        } catch (error) {
            console.error('Error removing right:', error);
            toast.error('Failed to remove right');
        }
    };

    const toggleRightStatus = async (right) => {
        try {
            await axios.put(`/api/admin/grievance-customer-rights/${right.id}`, {
                ...right,
                is_active: !right.is_active
            });
            fetchData();
        } catch (error) {
            console.error('Error toggling status:', error);
            toast.error('Failed to update status');
        }
    };

    if (loading) {
        return <div className="p-8 text-center text-slate-500 font-medium">Loading Commitment & Rights Settings...</div>;
    }

    return (
        <div className="space-y-8 max-w-6xl mx-auto">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Commitment & Rights</h1>
                    <p className="text-slate-500 mt-1">Manage our commitment text and customer rights</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Section Settings */}
                <div className="space-y-6">
                    <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100">
                        <div className="flex items-center gap-3 mb-6">
                            <ShieldCheckIcon className="h-6 w-6 text-primary-600" />
                            <h2 className="text-xl font-bold text-slate-800">Section Settings</h2>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                                <input
                                    type="text"
                                    value={settings.title}
                                    onChange={(e) => setSettings({ ...settings, title: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Section Description</label>
                                <textarea
                                    rows="4"
                                    value={settings.description}
                                    onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                                />
                            </div>
                            <div className="pt-4 border-t border-slate-50">
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Promise Title</label>
                                <input
                                    type="text"
                                    value={settings.promise_title}
                                    onChange={(e) => setSettings({ ...settings, promise_title: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none font-bold"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Promise Text</label>
                                <textarea
                                    rows="3"
                                    value={settings.promise_text}
                                    onChange={(e) => setSettings({ ...settings, promise_text: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                                />
                            </div>
                            <button
                                onClick={handleSettingsSave}
                                disabled={savingSettings}
                                className="w-full py-4 bg-primary-600 hover:bg-primary-700 text-white rounded-2xl font-bold transition-all shadow-lg shadow-primary-600/20 disabled:opacity-50 mt-4"
                            >
                                {savingSettings ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Customer Rights */}
                <div className="space-y-6">
                    <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100">
                        <div className="flex items-center gap-3 mb-6">
                            <CheckCircleIcon className="h-6 w-6 text-green-600" />
                            <h2 className="text-xl font-bold text-slate-800">Customer Rights</h2>
                        </div>

                        <form onSubmit={handleAddRight} className="mb-8">
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={newRight}
                                    onChange={(e) => setNewRight(e.target.value)}
                                    placeholder="Add a new customer right..."
                                    className="flex-1 px-4 py-3 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                                />
                                <button
                                    type="submit"
                                    disabled={addingRight || !newRight.trim()}
                                    className="p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-2xl shadow-lg transition-all disabled:opacity-50"
                                >
                                    <PlusIcon className="h-6 w-6" />
                                </button>
                            </div>
                        </form>

                        <div className="space-y-3">
                            {rights.map((right) => (
                                <div key={right.id} className="group flex items-start gap-3 p-4 bg-slate-50 hover:bg-white rounded-2xl border border-transparent hover:border-slate-100 transition-all hover:shadow-sm">
                                    <button
                                        onClick={() => toggleRightStatus(right)}
                                        className={`mt-1 h-5 w-5 rounded-md flex items-center justify-center transition-colors ${right.is_active ? 'bg-primary-500 text-white' : 'bg-slate-200 text-slate-400'}`}
                                    >
                                        <CheckCircleIcon className="h-4 w-4" />
                                    </button>
                                    <div className="flex-1">
                                        {editingRight?.id === right.id ? (
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    autoFocus
                                                    value={editingRight.text}
                                                    onChange={(e) => setEditingRight({ ...editingRight, text: e.target.value })}
                                                    className="flex-1 px-3 py-1 text-sm border border-primary-300 rounded-lg outline-none focus:ring-2 focus:ring-primary-100"
                                                />
                                                <button
                                                    onClick={() => handleUpdateRight(right)}
                                                    disabled={updatingRightId === right.id}
                                                    className="p-1 px-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                                                >
                                                    <CheckIcon className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => setEditingRight(null)}
                                                    className="p-1 px-2 bg-slate-400 text-white rounded-lg hover:bg-slate-500 transition-colors"
                                                >
                                                    <XMarkIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <p className={`text-sm font-medium leading-relaxed ${right.is_active ? 'text-slate-700' : 'text-slate-400 italic'}`}>
                                                {right.right_text}
                                            </p>
                                        )}
                                    </div>
                                    {!editingRight || editingRight.id !== right.id ? (
                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                            <button
                                                onClick={() => setEditingRight({ id: right.id, text: right.right_text })}
                                                className="p-2 text-slate-400 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-all"
                                            >
                                                <PencilIcon className="h-5 w-5" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteRight(right.id)}
                                                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                            >
                                                <TrashIcon className="h-5 w-5" />
                                            </button>
                                        </div>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, rightId: null })}
                onConfirm={confirmDeleteRight}
                title="Remove Customer Right"
                message="Are you sure you want to remove this customer right?"
            />
        </div>
    );
};

export default GrievanceCommitmentPage;
